/* eslint-disable */
import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import css from 'react-table/react-table.css';
// Direct imports (not the Widget barrel) so webpack doesn't drag the
// admin-only Widget components — FileExplorer, FileInput, EditorInput
// etc. — into the publication route's chunk graph. Going through the
// barrel pulls in those components transitively, which then forces
// admin-vendor.chunk.js (react-keyed-file-browser, react-dropzone-
// component, etc., ~129 KB) to load on every public publication view.
import AdCarousel from '../../components/Widget/Ad/AdCarousel';
import AdFixSection from '../../components/Widget/Ad/AdFixSection';
import LatestNewsList from '../../components/Widget/Other/LatestNewsList';
import NewsWithSquareImgList from '../../components/Widget/Other/NewsWithSquareImgList';
import VideoWithSquareImgList from '../../components/Widget/Other/VideoWithSquareImgList';
import Img from '../../components/Img';
import Link from '../../components/Link';
import SuppliersGallery from '../../components/Widget/Other/SuppliersGallery';
import Archives from '../archives/Archives';
import categoryMap from '../../common/categoryMap';
import s from './Publication.css';

// Issue names are sometimes saved as "SEAB May-Jun 2026" and sometimes as
// just "May-Jun 2026". On narrow grid tiles the prefix wraps to its own
// line which breaks alignment, and on a publication page the prefix is
// redundant anyway (the visitor already knows which magazine this is).
// Single source of truth so renderIssueCard + renderLatestIssueSection
// don't drift.
const PUB_PREFIX_RE = /^(SEAB|SEAC|BKT|LT|SST)\s+/i;
function stripPubPrefix(name) {
  return (name || '').replace(PUB_PREFIX_RE, '');
}

class Publication extends React.Component {
  static propTypes = {
    publication: PropTypes.object.isRequired,
    editor: PropTypes.object,
    associatePublisher: PropTypes.object,
    issues: PropTypes.arrayOf(PropTypes.object).isRequired,
    news: PropTypes.arrayOf(PropTypes.object).isRequired,
    events: PropTypes.arrayOf(PropTypes.object).isRequired,
    ads: PropTypes.arrayOf(PropTypes.object).isRequired,
    suppliers: PropTypes.arrayOf(PropTypes.object).isRequired,
    industrialLinks: PropTypes.arrayOf(PropTypes.object).isRequired,
    videos: PropTypes.arrayOf(PropTypes.object).isRequired,
    cat: PropTypes.string,
    onFetchNews: PropTypes.func.isRequired,
  };

  static defaultProps = {
    cat: null,
    editor: null,
    associatePublisher: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      news: props.news,
      cat: props.cat,
    };
    this.registerStickySections = this.registerStickySections.bind(this);
  }

  componentDidMount() {
    window.addEventListener('scroll', this.registerStickySections);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.registerStickySections);
  }

  onCategoryChange(cat) {
    const publicationId = this.props.publication && this.props.publication.id;
    if (!categoryMap[cat].isNewsCategory) {
      // if the category is not for news, not fetching anything
      this.setState({ cat });
      // return;
    } else {
      const catForFetch = cat === 'all' ? null : cat;
      this.props.onFetchNews(publicationId, catForFetch).then(newNews => {
        this.setState({
          news: newNews,
          cat,
        });
      });
    }
    const displayUrlLink =
      cat === 'all'
        ? `/publications/${publicationId}/`
        : `/publications/${publicationId}/news/category/${cat}`;
    window.history.pushState({}, '', displayUrlLink);
  }

  getCategory() {
    return this.state.cat ? this.state.cat : 'all';
  }

  getLinkUrl(url) {
    return url.indexOf('http') !== -1 ? url : `//${url}`;
  }

  registerStickySections() {
    window.addEventListener('scroll', () => {
      const { stickyLeftSection } = this;
      const pageTopOffSet = window.pageYOffset;
      // Note: class sticky classes are defined in custom.css, only for big screen > 1200px
      if (stickyLeftSection) {
        const { clientHeight, offsetTop } = stickyLeftSection;
        const stickyClass = 'custom-sticky-publication-left-sect';
        if (pageTopOffSet > clientHeight + offsetTop) {
          stickyLeftSection.classList.add(stickyClass);
        } else {
          stickyLeftSection.classList.remove(stickyClass);
        }
      }
    });
  }

  renderIssueCard(issue, idx) {
    const { name, thumbnail_uid, file_uid } = issue;
    const displayName = stripPubPrefix(name);
    if (thumbnail_uid && file_uid) {
      return (
        <div className={s.issueCard} key={`issue-card-${idx}`}>
          <a
            href={
              file_uid.indexOf('https') === -1
                ? `https://dxfpracgmlczp.cloudfront.net/${file_uid}`
                : file_uid
            }
            target="_blank"
            rel="noreferrer"
          >
            <Img
              uid={thumbnail_uid}
              alt={`${displayName} cover`}
              className={s.issueCardImg}
              width="210"
              height="280"
              loading="lazy"
            />
          </a>
          <span className={s.issueTitle}>{displayName}</span>
        </div>
      );
    }
    return <span key={`issue-card-nothing-${idx}`} />;
  }

  renderIssuesSection() {
    const { issues } = this.props;
    return (
      <div>{issues.map((issue, idx) => this.renderIssueCard(issue, idx))}</div>
    );
  }

  renderVideoSection(size) {
    const { videos } = this.props;
    return (
      <div className={s.otherNewsSection}>
        <div className={s.newSquareListContainer}>
          <VideoWithSquareImgList items={videos.slice(0, size)} />
        </div>
      </div>
    );
  }

  // this rendering function is copied from Widget/EventCalendar
  renderEventInfoBox(e) {
    const { id, banner_image_uid, start_date, end_date, location, url } = e;
    const startDateString = moment(
      start_date.substring(0, 10),
      'YYYY-MM-DD',
    ).format('MMM DD,YYYY');
    const endDateString = moment(
      end_date.substring(0, 10),
      'YYYY-MM-DD',
    ).format('MMM DD,YYYY');
    return (
      <a
        target="_blank"
        href={this.getLinkUrl(url)}
        key={`event-info-box-${id}`}
        rel="noreferrer"
      >
        <div
          className="custom-calendar-event-info-container"
          key={`event-box-${id}`}
          id={`event-box-${id}`}
        >
          {/* Event banners can be the LCP element when an event is at
              the top of the page — the audit measured a 3.3 MB animated
              gif here as Priority: Low. fetchpriority bumps it ahead of
              other resources, loading="eager" prevents lazy hold-back. */}
          <img
            className="custom-calendar-event-img"
            src={
              banner_image_uid && banner_image_uid.indexOf('https') === -1
                ? `https://dxfpracgmlczp.cloudfront.net/${banner_image_uid}`
                : banner_image_uid
            }
            alt="Calendar event"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
          <div className="custom-calendar-event-detail">
            <div className="event-title">{e.title}</div>
            <div className="event-location">
              <i className="fa fa-map-marker" /> {location}
            </div>
            <div className="timing-detail">
              <strong>FROM</strong> {startDateString}{' '}
              {start_date.substring(11, 16)}
            </div>
            <div className="timing-detail">
              <strong>TO</strong> {endDateString} {end_date.substring(11, 16)}
            </div>
          </div>
        </div>
      </a>
    );
  }

  renderPublicationDetailSection() {
    const { publication, editor, associatePublisher } = this.props;
    const { write_up, name: publicationName } = publication;
    return (
      <div className={s.pubDetailOuterContainer}>
        <div className={s.pubDetailContainer}>
          <div className={s.personelContainer}>
            {associatePublisher && (
              <div>
                <span className={s.personPosition}>ASSOCIATE PUBLISHER</span>
                <a
                  className={s.personName}
                  href={`mailto:${associatePublisher.email}`}
                  target="_top"
                >
                  {associatePublisher.first_name} {associatePublisher.last_name}
                </a>
              </div>
            )}
            {editor && (
              <div>
                <span className={s.personPosition}>EDITOR</span>
                <a
                  className={s.personName}
                  href={`mailto:${editor.email}`}
                  target="_top"
                >
                  {editor.first_name} {editor.last_name}
                </a>
              </div>
            )}
          </div>
        </div>
        <div className={s.publicationWriteUpContainer}>
          <div
            className={s.publicationWriteUp}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: write_up }}
          />
          <a
            className={s.mediaKitLink}
            href={`mailto: info@tradelinkmedia.com.sg?subject=Request%20Media%20Kit%20for%20${publicationName
              .replace(/&/g, '%26')
              .split(' ')
              .join('%20')}%20publication&body=Name:%0ACompany%20name:%0A`}
          >
            <i className="fa fa-book" />
            &nbsp;REQUEST MEDIA KIT
          </a>
        </div>
      </div>
    );
  }

  renderLatestIssueSection() {
    // get the latest issue
    const issue = this.props.issues[0];
    if (!issue) {
      return <span />;
    }
    const { name, thumbnail_uid, file_uid } = issue;
    const displayName = stripPubPrefix(name);
    return (
      <div className={s.latestIssueSection}>
        <div className={s.sectionTitle}>Our latest issue</div>
        <div className={s.latestIssueContentWrapper}>
          <a
            href={
              file_uid.indexOf('https') === -1
                ? `https://dxfpracgmlczp.cloudfront.net/${file_uid}`
                : file_uid
            }
            target="_blank"
            rel="noreferrer"
          >
            <Img
              uid={thumbnail_uid}
              alt={`${displayName} — latest issue cover`}
              className={s.latestIssueCardImg}
              width="210"
              height="280"
              fetchpriority="high"
            />
          </a>
          <span>{displayName}</span>
        </div>
      </div>
    );
  }

  renderLatestNewsSection(news) {
    const baseRoutingPath = `/publications/${this.props.publication.id}/news`;
    if (news.length > 0) {
      return (
        <div className={s.latestNewsSection}>
          <div className={s.sectionTitle}>Latest</div>
          <LatestNewsList items={news} baseRoute={baseRoutingPath} />
        </div>
      );
    }
    return <span />;
  }

  renderArchiveSection() {
    return (
      <div className={s.archiveSection}>
        <div className={s.sectionTitle}>Archive</div>
        <Archives publication={this.props.publication.id} />
      </div>
    );
  }

  renderOtherNewsSection(news) {
    const baseRoutingPath = `/publications/${this.props.publication.id}/news`;
    return (
      <div className={s.otherNewsSection}>
        <div className={s.newSquareListContainer}>
          <NewsWithSquareImgList items={news} baseRoute={baseRoutingPath} />
        </div>
      </div>
    );
  }

  renderOldNewsSection(news) {
    return (
      <div className={s.oldNewsSection}>
        <div className={s.sectionTitle}>More News</div>
        {news.map(item => {
          const { id, title } = item;
          const toPath = `/publications/${this.props.publication.id}/news/${id}`;
          return (
            <Link
              to={toPath}
              className={s.oldNewsLink}
              key={`news-list-item-${id}`}
            >
              {title}
            </Link>
          );
        })}
      </div>
    );
  }

  renderEventSection() {
    return (
      <div className={s.eventSection}>
        <div className={s.sectionTitle}>Upcoming Events</div>
        <div>{this.props.events.map(e => this.renderEventInfoBox(e))}</div>
      </div>
    );
  }

  renderSponsorSection() {
    const ads = this.props.ads
      .filter(ad => ad.type === 'normal')
      .map(ad => {
        let { image_uid } = ad;
        const { title, image_url } = ad;
        image_uid =
          image_uid && image_uid.indexOf('https') === -1
            ? `https://dxfpracgmlczp.cloudfront.net/${image_uid}`
            : image_uid;
        return {
          title,
          imgSrc: image_uid,
          link: image_url,
        };
      });
    if (ads.length === 0) return null;
    return (
      <div className={s.adsSection}>
        <div className={s.sectionTitle}>Industry Partners</div>
        <div>
          <AdCarousel items={ads} />
        </div>
      </div>
    );
  }

  renderAdsBannerSection(position, inLine) {
    const ads = this.props.ads
      .filter(ad => ad.type === 'premium' && ad.position === position)
      .map(ad => {
        let { image_uid } = ad;
        const { image_url } = ad;
        image_uid =
          image_uid && image_uid.indexOf('https') === -1
            ? `https://dxfpracgmlczp.cloudfront.net/${image_uid}`
            : image_uid;
        return {
          imgSrc: image_uid,
          imgUrl: image_url,
        };
      });
    if (ads && ads.length > 0) {
      return (
        <div className={inLine ? s.adsInlineSection : s.adsFixSection}>
          <AdFixSection ads={ads} />
        </div>
      );
    }
    return <span />;
  }

  renderCategoryButtonsSection() {
    const currentCat = this.getCategory();
    if (this.props.publication.youtube_playlist_id !== '0') {
      return (
        <div className={s.categoryButtonsSection}>
          {Object.keys(categoryMap).map(cat => {
            const btnClassName =
              cat === currentCat
                ? classNames(s.categoryButton, s.currentChosen)
                : s.categoryButton;
            return (
              <div className={s.categoryButtonWrapper} key={cat}>
                <button
                  className={btnClassName}
                  onClick={() => this.onCategoryChange(cat)}
                >
                  {categoryMap[cat].name}
                </button>
              </div>
            );
          })}
        </div>
      );
    }
    const newCategoryMap = JSON.parse(JSON.stringify(categoryMap));
    delete newCategoryMap.videos;
    return (
      <div className={s.categoryButtonsSection}>
        {Object.keys(newCategoryMap).map(cat => {
          const btnClassName =
            cat === currentCat
              ? classNames(s.categoryButton, s.currentChosen)
              : s.categoryButton;
          return (
            <div className={s.categoryButtonWrapper} key={cat}>
              <button
                className={btnClassName}
                onClick={() => this.onCategoryChange(cat)}
              >
                {categoryMap[cat].name}
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  renderNewsSection() {
    const { news: allNews, cat } = this.state;
    const allNewsLength = allNews.length;
    if (allNewsLength > 0) {
      const latestNews = allNews.slice(
        0,
        allNewsLength > 4 ? 4 : allNewsLength,
      );
      const otherNews =
        allNewsLength > 4
          ? allNews.slice(4, allNewsLength > 16 ? 16 : allNewsLength)
          : [];
      const otherNews1 =
        allNewsLength > 16
          ? allNews.slice(16, allNewsLength > 28 ? 28 : allNewsLength)
          : [];
      const oldNews =
        allNewsLength > 28 ? allNews.slice(28, allNewsLength) : [];
      if (this.props.publication.youtube_playlist_id !== '0') {
        return (
          <span>
            {this.renderLatestNewsSection(latestNews)}
            {
              <div className={s.otherNewsSection}>
                <div className={s.sectionTitle}>News</div>
              </div>
            }
            {otherNews.length > 0 && this.renderOtherNewsSection(otherNews)}
            {this.renderAdsBannerSection(1, true)}
            {otherNews1.length > 0 && this.renderOtherNewsSection(otherNews1)}
            {this.renderAdsBannerSection(2, true)}
            {
              <div className={s.otherNewsSection}>
                <div className={s.sectionTitle}>Videos</div>
              </div>
            }
            {this.renderVideoSection(9)}
            {oldNews.length > 0 && this.renderOldNewsSection(oldNews)}
          </span>
        );
      }
      return (
        <span>
          {this.renderLatestNewsSection(latestNews)}
          {
            <div className={s.otherNewsSection}>
              <div className={s.sectionTitle}>News</div>
            </div>
          }
          {otherNews.length > 0 && this.renderOtherNewsSection(otherNews)}
          {this.renderAdsBannerSection(1, true)}
          {otherNews1.length > 0 && this.renderOtherNewsSection(otherNews1)}
          {this.renderAdsBannerSection(2, true)}
          {oldNews.length > 0 && this.renderOldNewsSection(oldNews)}
        </span>
      );
    }
    return (
      <div className={s.noItemOfCatAnnounce}>
        There is no item in <strong>{categoryMap[cat].name}</strong> category
      </div>
    );
  }

  renderIndustialLinkSection() {
    const { suppliers, industrialLinks, publication } = this.props;
    return (
      <div className={s.industrialLinkSection}>
        <SuppliersGallery
          suppliers={suppliers}
          industrialLinks={industrialLinks}
          publication={publication.short_name}
        />
      </div>
    );
  }

  renderMainContent() {
    const { cat } = this.state;
    switch (cat) {
      case 'industry-links':
        return this.renderIndustialLinkSection();
      case 'magazines':
        return this.renderIssuesSection();
      case 'videos':
        return this.renderVideoSection(30);
      default:
        return (
          <div>
            {this.renderNewsSection()}
            {this.renderArchiveSection()}
          </div>
        );
    }
  }

  renderRightSection() {
    return (
      <div
        className={s.rightSection}
        ref={ref => {
          this.rightSection = ref;
        }}
      >
        {this.renderLatestIssueSection()}
        {this.renderSponsorSection()}
        {this.renderEventSection()}
      </div>
    );
  }

  renderMiddleSection(adsPosition) {
    const ads =
      this.props.ads
        .filter(ad => ad.type === 'premium' && ad.position === adsPosition)
        .map(ad => {
          let { image_uid } = ad;
          const { image_url } = ad;
          image_uid =
            image_uid && image_uid.indexOf('https') === -1
              ? `https://dxfpracgmlczp.cloudfront.net/${image_uid}`
              : image_uid;
          return {
            imgSrc: image_uid,
            imgUrl: image_url,
          };
        }) || [];
    return (
      <div
        className={s.middleSection}
        style={ads.length === 0 ? { paddingTop: 0 } : {}}
      >
        {this.renderAdsBannerSection(adsPosition, false)}
        {this.renderCategoryButtonsSection()}
        {this.renderMainContent()}
      </div>
    );
  }

  renderLeftSection() {
    return (
      <div className={s.leftSection}>
        <div
          ref={ref => {
            this.stickyLeftSection = ref;
          }}
        >
          {this.renderLatestIssueSection()}
          {this.renderPublicationDetailSection()}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className={s.container}>
        <span className={s.leftSectionTop}>{this.renderLeftSection()}</span>
        {this.renderMiddleSection(0 /* adsPosition */)}
        {this.renderRightSection()}
        <span className={s.leftSectionBottom}>{this.renderLeftSection()}</span>
      </div>
    );
  }
}

export default withStyles(s, css)(Publication);
